import { keyframes } from '@emotion/react';
import type { CenterProps, HeadingProps } from '@hover/blueprint';
import {
  Box,
  Center,
  FocusLock,
  Grid,
  Heading,
  IconButton,
  cssVariables,
  forwardRef,
  motion,
  useBoolean,
  useDisclosure,
  useEventListener,
  useTheme,
} from '@hover/blueprint';
import { iEye, iEyeOff, iconChevronLeft } from '@hover/icons';
import { AnimatePresence, MotionConfig } from 'framer-motion';
import type { PropsWithChildren } from 'react';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router';

import { DarkThemeColor } from 'src/modules/homeownerApp/components/meta/DarkThemeColor';
import { SafeRemoveScroll } from 'src/shared/components';
import type { ButtonProps } from 'src/shared/components/Button';
import { fadePresence } from 'src/shared/helpers/motion';
import {
  TranslationNamespace,
  useIsEmbed,
  useMemoContextValue,
  useTranslate,
} from 'src/shared/hooks';
import { buttonPressed, moduleViewed, pinchedZoom } from 'src/shared/telemetry';

import { isGeneratingImage, nudge } from '../../helpers';
import {
  useHomeownerAppContext,
  useIsFullscreenImageOpen,
  useRoutes,
} from '../../hooks';
import type { AiImage, AiOrSampleDisplayImage } from '../../types';
import {
  getImageId,
  getImagePath,
  isAiDisplay,
  isAiGenerated,
  isDisplay,
  isGenerated,
  isMyUpload,
  isSample,
} from '../../types';
import { TileIconButton } from '../TileButton';
import { Zoom } from '../Zoom';

import { CarouselControls } from './CarouselControls';
import { CustomizeTileIconButton } from './CustomizeTileIconButton';
import { DesignDetails } from './DesignDetails';
import { FullscreenImageCarousel } from './FullscreenImageCarousel';
import { MetaButtons } from './MetaButtons';
import { SaveTileIconButton } from './SaveTileIconButton';
import {
  FullscreenImageContentsProvider,
  latestFullscreenImage,
  useFullscreenImageContents,
} from './context';
import type { FullscreenImageContentsValue } from './context';
import {
  layoutBreakpoint,
  presenceVariantsWithDirection,
  useCarouselTransition,
  useFullscreenDepth,
  useFullscreenTransition,
} from './helpers';
import { useActiveFullscreenImage } from './useActiveFullscreenImage';
import { useImagePair } from './useImagePair';

const MotionGrid = motion(Grid);
const MotionCenter = motion(Center);

interface FullscreenImageHeadingProps extends Omit<HeadingProps, 'children'> {
  image: AiImage;
}

const FullscreenImageHeading = forwardRef<FullscreenImageHeadingProps, 'h1'>(
  ({ image, ...props }, ref) => {
    const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);

    let heading = t('fullscreenImage.myUpload');
    if (isGenerated(image)) {
      heading = image.style;
    }

    return (
      <Heading
        as="h1"
        color="inherit"
        size={{ base: 200, width250: 300 }}
        {...props}
        ref={ref}
      >
        {heading}
      </Heading>
    );
  },
);

const MotionFullscreenImageHeading = motion(FullscreenImageHeading);

const MobileFullscreenImageHeading = (props: CenterProps) => {
  const { image, direction } = useFullscreenImageContents();

  return (
    <Center
      display={{ base: 'inline', [layoutBreakpoint]: 'none' }}
      gridArea="top"
      placeSelf="center"
      width="fit-content"
      {...props}
    >
      <AnimatePresence custom={direction} initial={false} mode="popLayout">
        <MotionFullscreenImageHeading
          image={image.aiGeneratedImage}
          key={isAiDisplay(image) ? image.id : image.sampleId}
          noMargin
          size={{ base: 200, [layoutBreakpoint]: 300 }}
          transition={useCarouselTransition()}
          whiteSpace="nowrap"
          width="fit-content"
          {...presenceVariantsWithDirection(direction, {
            center: { opacity: 1, x: 0 },
            left: { opacity: 0, x: -40 },
            right: { opacity: 0, x: 40 },
          })}
        />
      </AnimatePresence>
    </Center>
  );
};

const ToggleOriginalButton = (
  props: ButtonProps & { isUnauthenticated?: boolean },
) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { isUnauthenticated, ...buttonProps } = props;
  const { currentMenu, trackingData, user } = useHomeownerAppContext();
  const { isShowingOriginalImage, toggleOriginalImage, tracking } =
    useFullscreenImageContents();

  useEventListener('keydown', event => {
    if (event.key === 'o' && !currentMenu) toggleOriginalImage();
  });

  const handleClick = () => {
    buttonPressed({
      ...trackingData,
      track_id: isShowingOriginalImage
        ? 'show-generated-toggle'
        : 'show-original-toggle',
      button_location: 'bottom-right-main-page',
      button_text: isShowingOriginalImage ? 'hide original' : 'show original',
      ai_image_watermarked:
        isShowingOriginalImage && (isUnauthenticated || !user?.isHomeowner),
      ...tracking,
    });

    toggleOriginalImage();
  };

  return (
    <TileIconButton
      aria-label={
        isShowingOriginalImage
          ? t('fullscreenImage.hideOriginal')
          : t('fullscreenImage.showOriginal')
      }
      icon={isShowingOriginalImage ? iEyeOff : iEye}
      label={t('fullscreenImage.original')}
      onClick={handleClick}
      {...buttonProps}
    />
  );
};

interface FullscreenImageCommonProps
  extends Pick<
    FullscreenImageContentsValue,
    'images' | 'generateNextImage' | 'disableCarousel'
  > {
  /**
   * Base route for the current fullscreen image, this is used to generate the
   * paths for the next and previous image when paging through images.
   *
   * @remarks the path must include `:fullscreenImageId` as the only route
   * parameter. Any additional parameters must be interpolated into the path
   * string.
   */
  path: string;
  /**
   * Route to return to when fullscreen is closed
   */
  returnPath: string;
  /**
   * Show the unauthenticated version of the fullscreen view which hides a
   * handful of interactions that are only available to authenticated users
   */
  isUnauthenticated?: boolean;
}

export interface FullscreenImageContentsProps
  extends Omit<CenterProps, 'style'>,
    FullscreenImageCommonProps {
  image: AiOrSampleDisplayImage;
  isLayoutAnimationComplete: boolean;
  onLayoutAnimationComplete: () => void;
}

const fadeIn = keyframes`
  0% { background: transparent };
  100% { background ${cssVariables.color('neutral.900')} }
`;

const FullscreenImageContents = forwardRef<FullscreenImageContentsProps, 'div'>(
  (
    {
      image,
      images,
      isLayoutAnimationComplete,
      generateNextImage,
      onLayoutAnimationComplete,
      path,
      returnPath,
      isUnauthenticated,
      disableCarousel,
      ...props
    },
    ref,
  ) => {
    const initialRenderRef = useRef(false);
    const [isZooming, setIsZooming] = useState(false);

    if (__DEV__) {
      if (initialRenderRef.current && !isLayoutAnimationComplete) {
        // A rerender during the layout animation causes animation performance
        // degradation. This is particularly noticeable on chrome on android
        // devices. Consider checking the value of `isLayoutAnimationComplete`
        // prior to any operation that would cause a rerender of this component
        // eslint-disable-next-line no-console
        console.warn(
          '🐢 FullscreenImage: component rendered more than once during layout animation',
        );
      }
      initialRenderRef.current = true;
    }

    const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
    const { trackingData, user } = useHomeownerAppContext();

    const {
      isOpen: isDesignDetailsOpen,
      onClose: onDesignDetailsClose,
      onOpen: onDesignDetailsOpen,
    } = useDisclosure();

    const imagePair = useImagePair(image, !isLayoutAnimationComplete);

    const generatedImage = image.aiGeneratedImage;

    const fullImageTrackingData = useMemo(() => {
      const baseTrackingData = {
        ...trackingData,
        ...(isAiGenerated(generatedImage)
          ? {
              ai_original_image_id: generatedImage.aiOriginalImageId,
              ai_generated_image_id: generatedImage.id,
            }
          : undefined),
        ...(isDisplay(image) && !isSample(image)
          ? {
              ai_display_image_id: image.id,
            }
          : undefined),
        ...(!isSample(image)
          ? {
              ai_image_watermarked: isUnauthenticated || !user?.isHomeowner,
            }
          : undefined),
      };

      if ('edits' in generatedImage && generatedImage.edits) {
        return {
          ...baseTrackingData,
          color_name: generatedImage.edits.walls?.paint?.name || undefined,
          color_hex: generatedImage.edits.walls?.paint?.hex,
          manufacturer:
            generatedImage.edits.walls?.paint?.manufacturer || undefined,
        };
      }
      return baseTrackingData;
    }, [
      generatedImage,
      trackingData,
      image,
      user?.isHomeowner,
      isUnauthenticated,
    ]);

    const handleDesignDetailsClick = () => {
      if ('edits' in generatedImage && generatedImage.edits) {
        moduleViewed({
          ...fullImageTrackingData,
          track_id: 'design-details',
        });
      }

      onDesignDetailsOpen();
    };

    const { tracking } = imagePair;
    const history = useHistory();

    const isSampleImage = isSample(image);
    const closeModal = () => {
      history.push(returnPath);

      buttonPressed({
        ...trackingData,
        track_id: isGenerated(image)
          ? 'close-full-screen-image-view'
          : 'exit-original-image-full-screen',
        button_text: 'close-icon',
        button_location: 'top-left-main-page',
        ai_image_watermarked:
          !isSampleImage && (isUnauthenticated || !user?.isHomeowner),
        ...tracking,
      });
    };

    useEventListener('keydown', event => {
      if (event.key === 'Escape') closeModal();
    });

    const transition = useFullscreenTransition();
    const zIndex = useFullscreenDepth();
    const isFullscreenImageOpen = useIsFullscreenImageOpen();

    const areas = {
      base: `
        'top      top     top'
        'center center center'
        'left   bottom  right'
      `,
      [layoutBreakpoint]: `
        'top    top    top'
        '.    center     .'
        'left bottom right'
      `,
    };

    const column = 'minmax(3rem, 1fr)';
    const columns = {
      base: `auto 1fr auto`,
      [layoutBreakpoint]: `${column} auto ${column}`,
    };

    const rows = {
      base: 'auto 1fr auto',
      [layoutBreakpoint]: 'min-content auto min-content',
    };

    const isDisplayImage = isDisplay(image);
    const isGeneratedImage = isGenerated(image);
    const isMyUploadImage = isMyUpload(image);

    const canInteractWithImage =
      !isUnauthenticated &&
      ((isDisplayImage && !isMyUploadImage) || isGeneratedImage);

    const generatedImageLoaded =
      (isDisplayImage && !isGeneratingImage(image.aiGeneratedImage)) ||
      (isGeneratedImage && !isGeneratingImage(image));

    const { space } = useTheme();

    const value = useMemoContextValue({
      image,
      images,
      imagePair,
      generateNextImage,
      isLayoutAnimationComplete,
      onLayoutAnimationComplete,
      disableCarousel,
      path,
      tracking,
    });

    const { isFirstRoute, wasCustomizeRoute } = useRoutes();
    return (
      <FullscreenImageContentsProvider value={value}>
        <MotionConfig transition={transition}>
          <Center
            aria-modal
            bottom={0}
            color="neutral.0"
            left={0}
            position="fixed"
            ref={ref}
            role="dialog"
            tabIndex={-1}
            top={0}
            // NOTE: we need to set the width like this instead of using `right`
            // otherwise removing the scrollbar via `RemoveScroll` causes a
            // weird bug where the image doesn't render initially
            width="100vw"
            zIndex={zIndex}
            {...props}
          >
            <MotionGrid
              animation={
                !isFirstRoute && !wasCustomizeRoute
                  ? `${fadeIn} 0.2s ease-out`
                  : undefined
              }
              bg={isFullscreenImageOpen ? 'neutral.900' : 'transparent'}
              boxSizing="border-box"
              exit={{ opacity: 0 }}
              flexDirection="row"
              height="100%"
              justifyContent="center"
              key="background"
              pb={{
                base: `calc(env(safe-area-inset-bottom, 0px) + ${space[500]})`,
                [layoutBreakpoint]: `calc(env(safe-area-inset-bottom, 0px) + ${space[600]})`,
                tablet: 900,
              }}
              position="relative"
              pt={{
                base: `calc(env(safe-area-inset-top, 0px) + ${space[400]})`,
                [layoutBreakpoint]: `calc(env(safe-area-inset-bottom, 0px) + ${space[500]})`,
                tablet: 600,
              }}
              px={{ base: 0, [layoutBreakpoint]: 600, tablet: 900 }}
              rowGap="300"
              style={{ transition: 'all .5s ease' }}
              templateAreas={areas}
              templateColumns={columns}
              templateRows={rows}
              width="100%"
            >
              {isZooming && (
                <Zoom
                  backgroundColor="neutral.900"
                  image={getImagePath(image)}
                  onCloseClick={() => setIsZooming(false)}
                />
              )}
              <FullscreenImageCarousel
                activateZoom={() => {
                  setIsZooming(true);
                  pinchedZoom({
                    ...fullImageTrackingData,
                    track_id: 'zoom-in',
                  });
                }}
                display={isZooming ? 'none' : 'flex'}
                gridArea="center"
                placeSelf="center"
              />
              {!isZooming && (
                <IconButton
                  alignSelf="center"
                  colorScheme="light"
                  gridArea="top"
                  icon={iconChevronLeft}
                  iconSize="medium"
                  justifySelf="start"
                  label={t('fullscreenImage.close')}
                  ml={{ base: 500, [layoutBreakpoint]: 0 }}
                  onClick={closeModal}
                  size={{
                    base: 'small',
                    [layoutBreakpoint]: 'medium',
                    tablet: 'large',
                  }}
                  variant="ghost"
                  {...nudge(
                    { base: 0, [layoutBreakpoint]: 300, tablet: 500 },
                    'left',
                  )}
                />
              )}
              {canInteractWithImage && !isZooming && (
                <MetaButtons
                  disabled={!generatedImageLoaded}
                  gridArea="top"
                  image={image}
                  justifySelf="end"
                  mr={{ base: 500, [layoutBreakpoint]: 0 }}
                  onDesignDetailsClick={handleDesignDetailsClick}
                  zIndex={zIndex}
                />
              )}
              <MobileFullscreenImageHeading color="neutral.0" />
              <MotionCenter
                alignItems="center"
                gap="300"
                gridArea="bottom"
                {...fadePresence}
              >
                <ToggleOriginalButton
                  disabled={!imagePair.toggleImage}
                  isUnauthenticated={isUnauthenticated}
                  width="72px"
                />

                {!isSampleImage && !isUnauthenticated && (
                  <CustomizeTileIconButton
                    disabled={!generatedImageLoaded}
                    image={image}
                    trackingData={{
                      track_id: 'customize-full-screen',
                      view_type: 'full-screen-view',
                    }}
                    width="72px"
                  />
                )}
                {!isUnauthenticated && (
                  <SaveTileIconButton
                    disabled={isMyUploadImage || !generatedImageLoaded}
                    image={image}
                    width="72px"
                  />
                )}
              </MotionCenter>
              {images && images.length > 0 && (
                <CarouselControls
                  gridColumn="1/4"
                  gridRow="2/2"
                  initial={!isFirstRoute}
                  // Setting `isVisible` here may seem redundant, but it's necessary
                  // to fade the controls out when the fullscreen view is closed
                  isVisible={isFullscreenImageOpen}
                  left={{ base: 600, [layoutBreakpoint]: 0 }}
                  right={{ base: 600, [layoutBreakpoint]: 0 }}
                  top="50%"
                />
              )}
            </MotionGrid>
          </Center>
        </MotionConfig>
        {canInteractWithImage && (
          <DesignDetails
            image={image}
            isOpen={isDesignDetailsOpen}
            onClose={onDesignDetailsClose}
          />
        )}
      </FullscreenImageContentsProvider>
    );
  },
);

/**
 * The inner fullscreen image component, which is memoized because any
 * unnecessary renders can cause performance issues when animating into the
 * fullscreen state. See below for the custom props comparison function.
 */
const FullscreenImageContentsMemo = memo(
  FullscreenImageContents,
  (
    { image: previousImage, images: previousImages, ...previousProps },
    { image: nextImage, images: nextImages, ...nextProps },
  ) =>
    previousImage === nextImage &&
    !!previousImages?.every(
      (previous, i) => getImageId(previous) === getImageId(nextImages?.[i]),
    ) &&
    Object.entries(previousProps).every(([key, value]) =>
      Object.is(value, nextProps[key as keyof typeof previousProps]),
    ),
) as typeof FullscreenImageContents;

export interface FullscreenImageProps
  extends FullscreenImageCommonProps,
    PropsWithChildren<{}> {
  /**
   * Hide the page content behind the fullscreen image view when transitioning
   * from the customize route to fullscreen.
   *
   * @defaultValue false
   *
   * @remarks Before the fullscreen image layout and customize layout were
   * almost identical, we were running a layout animation to animate the image
   * placement when exiting customize to fullscreen. When that was the case,
   * rendering the content behind fullscreen view caused performance issues in
   * the layout animation. We no longer have much of a transtion between the
   * pages, so this isn't necessary, but I'm preserving it as a prop in case we
   * run into similar performance issues in the future.
   */
  hidePageContentsWhenAnimatingFromCustomize?: boolean;
}

const FullscreenImage = ({
  children,
  hidePageContentsWhenAnimatingFromCustomize = false,
  ...props
}: FullscreenImageProps) => {
  const isFullscreen = useIsFullscreenImageOpen();
  const { image, disableCarousel } = useActiveFullscreenImage({
    images: props?.images,
  });

  const isOpen = !!image;

  const isEmbed = useIsEmbed();
  const { isFirstRoute, wasCustomizeRoute } = useRoutes();

  const [initial, setInitial] = useState(!isFirstRoute);

  // There are a couple of `AnimatePresence` animations that we don't want to
  // run when this route is hit directly as opposed to transitioning from a list
  // view to fullscreen. This piece of state ensures they only run then.
  useEffect(() => {
    if (!isFullscreen) setInitial(true);
  }, [isFullscreen]);

  useEffect(() => {
    if (!isFirstRoute) return;

    // If `isFirstRoute` is `true`, that means the page was hit directly and
    // therefore won't run the layout animation. We usually update the latest
    // image when that animation completes, so set it here instead.
    latestFullscreenImage(image?.aiGeneratedImage);
  }, [image, isFirstRoute]);

  const [
    isLayoutAnimationComplete,
    { on: onLayoutAnimationComplete, off: resetIsLayoutAnimationComplete },
  ] = useBoolean(isFirstRoute);

  useEffect(() => {
    if (!isFullscreen) resetIsLayoutAnimationComplete();
  }, [isFullscreen, resetIsLayoutAnimationComplete]);

  const isAnimatingFromCustomize =
    wasCustomizeRoute && !isLayoutAnimationComplete;

  // Hide the content behind the fullscreen modal until the layout animation is
  // complete when navigating from customize to fullscreen to mitigate any
  // performance issues caused by rendering that's hidden under this element.
  const renderContent =
    (hidePageContentsWhenAnimatingFromCustomize && !isFullscreen) ||
    !isAnimatingFromCustomize;

  return (
    <>
      {renderContent && children}
      {isOpen && <DarkThemeColor />}
      <AnimatePresence initial={initial}>
        {isOpen && (
          <Box
            as={SafeRemoveScroll}
            enabled={isEmbed ? false : undefined}
            position="fixed"
            zIndex="overlay"
          >
            <FocusLock autoFocus={false} restoreFocus>
              <FullscreenImageContentsMemo
                disableCarousel={disableCarousel}
                image={image}
                isLayoutAnimationComplete={isLayoutAnimationComplete}
                onLayoutAnimationComplete={onLayoutAnimationComplete}
                {...props}
              />
            </FocusLock>
          </Box>
        )}
      </AnimatePresence>
    </>
  );
};

export { FullscreenImage };
